import React, { useEffect, useMemo, useState } from 'react';
import { ApiService } from "../../../services/ApiService"
import { createForm,  Form } from 'models/forms/Form';
import { FormElement } from 'models/forms/formElements/FormElement';
import FormElementListEditing from './FormElementListEditing';
import { utilities } from 'utils/utilities';
import { FormViewer } from '../FormViewer';
import { FormPreview } from './FormPreview';

interface FormEditorProps {
  formId: string;
  onSaveNewOrDelete: () => void;
}

const FormEditor: React.FC<FormEditorProps> = ({ formId, onSaveNewOrDelete }) => {

  const [form, setForm] = useState<Form | null>(createForm(utilities.DEFAULT_GUID, "Client Intake Form", '', []));
  const [formElements, setFormElements] = useState<FormElement[]>(form?.elements ?? [])
  const [title, setTitle] = useState<string>(form?.name ?? '');
  const [isChanged, setIsChanged] = useState<boolean>(true);

  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'saving' | 'success' | 'error' | null>(null);
  const [saveMessage, setSaveMessage] = useState<string>('');

  useEffect(() => {
        const loadForm = async () => {
          if (formId == utilities.DEFAULT_GUID) return;
          try{
              const data = (await ApiService.getForm(formId)).data;
              setForm(data);
              setTitle(data?.name ?? '');
              setFormElements(data?.elements ?? [])
          }catch(error){
            setForm(createForm(utilities.DEFAULT_GUID, "Client Intake Form", '', []));
            setTitle('Client Intake Form');
            setFormElements([]);
            console.error(error);
          }
        };
        loadForm();
  }, [formId]);

  const updatedForm = useMemo(
    () => ({
      ...(form ?? createForm(utilities.DEFAULT_GUID, title ?? '', '', [])),
      id: form?.id ?? utilities.DEFAULT_GUID,
      name: title,
      description: form?.description ?? '',
      elements: formElements
    }),
    [form, title, formElements]
  );

  const handleSave = async () => {
    if (saveStatus === 'saving') return;

    setIsSaveModalOpen(true);
    setSaveStatus('saving');
    setSaveMessage('Saving form...');

    try {
      const formToSave: Form = {
        ...(updatedForm as Form),
        id: formId === utilities.DEFAULT_GUID ? utilities.DEFAULT_GUID : formId,
        name: title || '',
        elements: formElements,
      };

      const isNew = formId === utilities.DEFAULT_GUID;
      const response = isNew
        ? await ApiService.saveForm(formToSave)
        : await ApiService.updateForm(formToSave);

      setForm(formToSave);
      setIsChanged(false);
      setSaveStatus('success');
      setSaveMessage('Saved successfully.');

      // Refresh the left-side list (after save completes)
      onSaveNewOrDelete();
    } catch (error: any) {
      setSaveStatus('error');
      setSaveMessage(error?.message ? `Save failed: ${error.message}` : 'Save failed.');
      console.error('Error saving form:', error);
    }
  };
  
  return (
    <div className="flex flex-row gap-6 items-start p-6 bg-gray-50">
      {isSaveModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white w-full max-w-sm p-6 rounded-lg shadow-lg">
            <h3 className="text-lg font-semibold mb-2">Form Save</h3>
            <p className="text-sm text-gray-700 mb-4">{saveMessage}</p>

            <div className="flex justify-end gap-3">
              {saveStatus === 'saving' ? (
                <button
                  type="button"
                  disabled
                  className="px-4 py-2 bg-gray-200 text-gray-600 rounded"
                >
                  Saving...
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setIsSaveModalOpen(false);
                    setSaveStatus(null);
                    setSaveMessage('');
                  }}
                  className={
                    saveStatus === 'success'
                      ? 'px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700'
                      : 'px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-800'
                  }
                >
                  {saveStatus === 'success' ? 'OK' : 'Close'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Left Side: Form Editor Box */}
      <div className="w-full max-w-md bg-white border border-gray-300 rounded-lg shadow-sm p-6">
        <h3 className="text-xl font-semibold mb-3">Form Editor</h3>
        <h4 className="text-md font-medium mb-3">{title}</h4>
  
        <label htmlFor={form?.id} className="block mb-1 text-sm text-gray-700">
          Title:
        </label>
        <input
          type="text"
          onChange={(event) => setTitle(event.target.value)}
          value={title}
          className="block mb-4 px-3 py-2 border border-gray-300 rounded w-full"
        />
  
        <FormElementListEditing
          setFormElements={setFormElements}
          formElements={formElements}
          onSave={handleSave}
          isEditing={true}
        />
  
        <button
          type="button"
          onClick={handleSave}
          disabled={saveStatus === 'saving'}
          className={
            saveStatus === 'saving'
              ? 'mt-6 px-4 py-2 bg-gray-300 text-gray-600 rounded cursor-not-allowed'
              : 'mt-6 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700'
          }
        >
          Save Form
        </button>
      </div>
  
      {/* Right Side: Form Preview Box */}
      <div className="flex-1 bg-white border border-gray-300 rounded-lg shadow-sm p-6">
        <h4 className="text-xl font-semibold mb-4">Form Preview</h4>
        <FormPreview form={updatedForm} />
      </div>
    </div>
  );
  
    
}

export default FormEditor;
